'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { styles } from './UiKitTagGroup.styles';

// Интерфейс минимальных пропсов, которые должен иметь каждый тег
interface TagProps<T> {
  value?: T;
  selected?: boolean;
  onSelectionChange?: (key: T, selected: boolean) => void;
}

export interface SingleSelectionModeProps<T> {
  selectionMode: 'single';
  value?: T | null;
  onSelectionChange: (key: T | null) => void;
}

export interface MultipleSelectionModeProps<T> {
  selectionMode: 'multiple';
  value?: T[];
  onSelectionChange: (keys: T[]) => void;
}

export type SelectionModeProps<T> =
  | SingleSelectionModeProps<T>
  | MultipleSelectionModeProps<T>;

export type UiKitTagGroupProps<T> = SelectionModeProps<T> & {
  children: React.ReactNode;
  className?: string;
};

export function UiKitTagGroup<T>({
  children,
  className,
  selectionMode,
  value,
  onSelectionChange
}: UiKitTagGroupProps<T>) {
  const controlledValue = useMemo(() => {
    if (selectionMode === 'single') {
      return value ? [value] : [];
    }

    return value ?? [];
  }, [selectionMode, value]);

  const [selectedKeys, setSelectedKeys] = useState<Set<T>>(
    new Set(controlledValue)
  );

  useEffect(() => {
    setSelectedKeys(new Set(controlledValue));
  }, [controlledValue]);

  const handleSelectionChange = (key: T, selected: boolean) => {
    const newSelectedKeys = new Set(selectedKeys);

    if (selectionMode === 'single') {
      if (selected) {
        newSelectedKeys.clear();
        newSelectedKeys.add(key);
      } else {
        newSelectedKeys.delete(key);
      }

      setSelectedKeys(newSelectedKeys);
      onSelectionChange(Array.from(newSelectedKeys)[0] ?? null);

      return;
    }

    if (selected) {
      newSelectedKeys.add(key);
    } else {
      newSelectedKeys.delete(key);
    }

    setSelectedKeys(newSelectedKeys);
    onSelectionChange(Array.from(newSelectedKeys));
  };

  // Клонируем дочерние элементы и передаем им пропсы
  const childrenWithProps = React.Children.map(children, (child) => {
    if (
      React.isValidElement<TagProps<T>>(child) &&
      child.props.value !== undefined
    ) {
      return React.cloneElement<TagProps<T>>(
        child as React.ReactElement<TagProps<T>>,
        {
          selected: selectedKeys.has(child.props.value),
          onSelectionChange: handleSelectionChange
        }
      );
    }
    return child;
  });

  const slots = styles();

  return (
    <div
      className={slots.base({ className })}
      role="group"
      aria-orientation="horizontal"
    >
      {childrenWithProps}
    </div>
  );
}
